<template>
  <v-container fluid class="erp-page page">
    <div class="erp-page-header page-hero">
      <div>
        <h2 class="d-flex align-center">Roles y Permisos <HelpHint text="Define qué puede hacer cada perfil. Recomendado: mínimo privilegio por módulo y operación." /></h2>
        <p>Gestiona los perfiles del sistema y los accesos de seguridad.</p>
      </div>
      <div class="d-flex align-center gap-2">
        <v-btn color="primary" class="mr-2" @click="openCreate">
          <v-icon left>mdi-plus</v-icon> Nuevo Rol
        </v-btn>
        <v-btn text color="primary" :loading="loading" @click="loadAll">Recargar</v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

    <v-row dense>
      <v-col cols="12" lg="8">
        <v-card outlined class="page-card">
          <v-card-title class="subtitle-1">Roles Configurados</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="roleHeaders"
              :items="roles"
              :loading="loading"
              item-key="id"
              dense
              hide-default-footer
              disable-pagination
            >
              <template v-slot:item.users="{ item }">
                <v-chip x-small outlined color="blue-grey">{{ usersByRole(item.name).length }}</v-chip>
              </template>
              <template v-slot:item.permissionsCount="{ item }">
                <v-chip x-small outlined color="indigo">{{ (item.permissions || []).length }}</v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="primary" @click="openEdit(item)" title="Editar Rol y Permisos">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card outlined class="page-card">
          <v-card-title class="subtitle-1">Usuarios Asociados por Rol</v-card-title>
          <v-card-text>
            <v-expansion-panels flat class="border-radius-panel">
              <v-expansion-panel v-for="role in roles" :key="role.id" class="border-bottom">
                <v-expansion-panel-header class="py-3">
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text--primary">{{ role.name }}</span>
                    <span class="caption text--secondary mt-0.5">({{ usersByRole(role.name).length }} usuarios)</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-0 pt-2">
                  <div v-if="usersByRole(role.name).length === 0" class="caption text--secondary font-italic px-3">Sin usuarios asignados</div>
                  <div class="d-flex flex-wrap px-2 pb-2">
                    <v-chip
                      v-for="user in usersByRole(role.name)"
                      :key="`${role.id}-${user.id}`"
                      class="ma-1"
                      small
                      outlined
                      color="blue-grey lighten-1"
                    >
                      <v-icon left small>mdi-account</v-icon>
                      {{ user.username || user.email }}
                    </v-chip>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- DIALOGO FORMULARIO DE ROL (NUEVO / EDITAR) -->
    <v-dialog v-model="dialog" max-width="600" scrollable>
      <v-card class="rounded-lg">
        <v-card-title class="headline grey lighten-4 border-bottom py-3">
          {{ isEditing ? 'Editar Perfil y Permisos' : 'Crear Nuevo Perfil' }}
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 65vh">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Nombre del Rol *"
                  outlined
                  dense
                  placeholder="ej. Facturador, Vendedor"
                  :rules="[v => !!v || 'El nombre es obligatorio']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.description"
                  label="Descripción corta"
                  outlined
                  dense
                  placeholder="Breve descripción"
                />
              </v-col>
            </v-row>

            <v-divider class="my-3" />
            
            <div class="d-flex align-center justify-between mb-2">
              <span class="subtitle-2 font-weight-bold">Accesos por menú y operación</span>
              <v-spacer />
              <span class="caption indigo--text font-weight-medium">{{ form.permissionIds.length }} seleccionados</span>
            </div>

            <v-text-field
              v-model="dialogPermissionFilter"
              label="Buscar menú, submenú o permiso..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="mb-3 bg-light"
            />

            <div class="permissions-grouped-container mt-2">
              <div v-if="groupedDialogPermissions.length === 0" class="caption text-center py-6 grey--text">
                No hay permisos que coincidan con tu búsqueda
              </div>
              <v-expansion-panels v-else accordion flat class="grouped-panels border rounded-lg">
                <v-expansion-panel
                  v-for="(group, gIdx) in groupedDialogPermissions"
                  :key="gIdx"
                  class="border-bottom"
                >
                  <v-expansion-panel-header class="py-3 px-4 font-weight-bold grey lighten-5">
                    <div class="d-flex align-center">
                      <v-icon color="primary" class="mr-2" size="20">{{ group.icon }}</v-icon>
                      <span class="subtitle-2 text--primary">{{ group.name }}</span>
                      <v-chip x-small color="grey lighten-3" class="ml-2 text--secondary font-weight-bold">
                        {{ group.items.length }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="px-2 pt-2">
                    <div class="permissions-list">
                      <div
                        v-for="perm in group.items"
                        :key="perm.id"
                        class="permission-row d-flex align-center py-2 px-3 border-radius-md hover-bg mb-1"
                      >
                        <div class="flex-grow-1 pr-3">
                          <div class="body-2 font-weight-bold text--primary">
                            {{ formatCodeToTitle(perm.code) }}
                          </div>
                          <div class="caption text--secondary mt-1">
                            {{ perm.description || 'Permite acceder a esta función del sistema.' }}
                          </div>
                        </div>
                        <v-switch
                          v-model="form.permissionIds"
                          :value="perm.id"
                          hide-details
                          dense
                          color="success"
                          class="ma-0 pa-0"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-form>
          <v-alert v-if="saveError" type="error" dense text class="mt-3 mb-0">{{ saveError }}</v-alert>
        </v-card-text>
        <v-card-actions class="grey lighten-4 border-top pa-3">
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="saveRole" class="px-6 font-weight-bold">
            Guardar Perfil
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rolesService } from "../services/roles";
import { usersService } from "../services/users";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "SecurityRoles",
  components: { HelpHint },
  data() {
    return {
      loading: false,
      error: "",
      roles: [],
      users: [],
      permissions: [],
      permissionFilter: "",
      
      // Dialog State
      dialog: false,
      isEditing: false,
      saving: false,
      saveError: "",
      valid: false,
      dialogPermissionFilter: "",
      form: {
        id: "",
        name: "",
        description: "",
        permissionIds: [],
      },

      roleHeaders: [
        { text: "Nombre del Rol", value: "name", class: "font-weight-bold" },
        { text: "Descripción", value: "description" },
        { text: "Usuarios", value: "users", align: "center" },
        { text: "Permisos", value: "permissionsCount", align: "center" },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
      permissionHeaders: [
        { text: "Permiso", value: "code", class: "font-weight-bold" },
        { text: "Descripción", value: "description", class: "font-weight-bold" },
      ],
    };
  },
  computed: {
    filteredPermissions() {
      const q = String(this.permissionFilter || "").trim().toLowerCase();
      if (!q) return this.permissions;
      return this.permissions.filter((p) => {
        const code = String(p.code || "").toLowerCase();
        const desc = String(p.description || "").toLowerCase();
        return code.includes(q) || desc.includes(q);
      });
    },
    filteredDialogPermissions() {
      const q = String(this.dialogPermissionFilter || "").trim().toLowerCase();
      if (!q) return this.permissions;
      return this.permissions.filter((p) => {
        const code = String(p.code || "").toLowerCase();
        const desc = String(p.description || "").toLowerCase();
        return code.includes(q) || desc.includes(q);
      });
    },
    groupedDialogPermissions() {
      const list = this.filteredDialogPermissions;
      const groups = {
        widgets: { name: "Dashboard y Widgets", icon: "mdi-view-dashboard-outline", items: [] },
        sales: { name: "Ventas y Facturación", icon: "mdi-cash-register", items: [] },
        purchases: { name: "Compras y Proveedores", icon: "mdi-truck-delivery-outline", items: [] },
        stock: { name: "Stock e Inventario", icon: "mdi-warehouse", items: [] },
        pricing: { name: "Gestión de Precios", icon: "mdi-tag-multiple-outline", items: [] },
        security: { name: "Seguridad y Auditoría", icon: "mdi-shield-check-outline", items: [] },
        config: { name: "Configuración General", icon: "mdi-cog-outline", items: [] },
        others: { name: "Otros Permisos", icon: "mdi-key-outline", items: [] },
      };

      list.forEach(p => {
        const code = String(p.code || "").toLowerCase();
        if (code.startsWith("widget:")) {
          groups.widgets.items.push(p);
        } else if (code.startsWith("sales.") || code.startsWith("sales:") || code.startsWith("invoice.") || code.startsWith("invoice:") || code.startsWith("budget.") || code.startsWith("budget:") || code.includes("factur")) {
          groups.sales.items.push(p);
        } else if (code.startsWith("purchases.") || code.startsWith("purchases:") || code.startsWith("supplier.") || code.startsWith("supplier:") || code.includes("compra") || code.includes("proveedor")) {
          groups.purchases.items.push(p);
        } else if (code.startsWith("stock.") || code.startsWith("stock:") || code.startsWith("wms.") || code.startsWith("wms:") || code.includes("inventario")) {
          groups.stock.items.push(p);
        } else if (code.startsWith("pricing.") || code.startsWith("pricing:") || code.startsWith("price.") || code.startsWith("price:") || code.includes("precio")) {
          groups.pricing.items.push(p);
        } else if (code.startsWith("security:") || code.startsWith("audit:") || code.includes("seguridad") || code.includes("auditoria")) {
          groups.security.items.push(p);
        } else if (code.startsWith("config:") || code.startsWith("email:") || code.startsWith("afip:") || code.includes("config")) {
          groups.config.items.push(p);
        } else {
          groups.others.items.push(p);
        }
      });

      return Object.values(groups).filter(g => g.items.length > 0);
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    formatCodeToTitle(code) {
      if (!code) return "Acceso";
      
      const custom = {
        "sales.current_accounts.view": "Ventas · Clientes y Cuentas",
        "widget:sellerRanking:view": "Ver Ranking de Vendedores (30d)",
        "widget:dailyFacturation:view": "Ver Caja Facturada Hoy",
        "widget:topCounterQueries:view": "Ver Top de Consultas Mostrador",
        "widget:priceChangesToday:view": "Ver Cambios de Precio Semanales",
        "widget:criticalStock:view": "Ver Stock Crítico"
      };
      
      if (custom[code]) return custom[code];
      
      return code
        .split(":")
        .map(word => {
          if (word === "view") return "Ver";
          if (word === "create") return "Crear";
          if (word === "edit" || word === "update") return "Editar";
          if (word === "delete") return "Eliminar";
          if (word === "manage") return "Gestionar";
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
    },
    usersByRole(roleName) {
      return (this.users || []).filter((u) => Array.isArray(u.roles) && u.roles.some((r) => r.name === roleName));
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [rolesRes, permissionsRes, usersRes] = await Promise.all([
          rolesService.getAll(),
          rolesService.getPermissions(),
          usersService.getAll(),
        ]);
        this.roles = this.unwrap(rolesRes) || [];
        this.permissions = this.unwrap(permissionsRes) || [];
        this.users = this.unwrap(usersRes) || [];
      } catch (error) {
        this.error = parseApiError(error, "No pudimos cargar los datos de seguridad.");
      } finally {
        this.loading = false;
      }
    },
    togglePerm(id) {
      const index = this.form.permissionIds.indexOf(id);
      if (index > -1) {
        this.form.permissionIds.splice(index, 1);
      } else {
        this.form.permissionIds.push(id);
      }
    },
    openCreate() {
      this.isEditing = false;
      this.saveError = "";
      this.dialogPermissionFilter = "";
      this.form = {
        id: "",
        name: "",
        description: "",
        permissionIds: [],
      };
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    openEdit(role) {
      this.isEditing = true;
      this.saveError = "";
      this.dialogPermissionFilter = "";
      this.form = {
        id: role.id,
        name: role.name,
        description: role.description || "",
        permissionIds: (role.permissions || []).map((p) => p.id),
      };
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    async saveRole() {
      const isValid = this.$refs.form.validate();
      if (!isValid) return;

      this.saving = true;
      this.saveError = "";
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description || "",
          permissionIds: this.form.permissionIds || [],
        };

        if (this.isEditing) {
          await rolesService.update(this.form.id, payload);
        } else {
          await rolesService.create(payload);
        }

        this.dialog = false;
        await this.loadAll();
      } catch (err) {
        this.saveError = parseApiError(err, "Ocurrió un error inesperado al intentar guardar el perfil.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.page-hero { background: var(--bg-card); border-radius: 16px; padding: 24px 28px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
.page-hero h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.02em; }
.page-hero p { margin: 0; color: var(--text-secondary); font-size: 14px; line-height: 1.5; }
.page-card { background: var(--bg-card); border-radius: 16px; padding: 12px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }

.border-radius-panel { border-radius: 8px; overflow: hidden; }
.border-bottom { border-bottom: 1px solid #eeeeee; }
.border-top { border-top: 1px solid #eeeeee; }
.bg-light { background-color: #fafafa; }

.permissions-grouped-container {
  max-height: 380px;
  overflow-y: auto;
}
.grouped-panels {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
  background: transparent !important;
}
.permission-row {
  transition: all 0.2s ease;
  border-radius: 8px;
  border: 1px solid transparent;
}
.permission-row:hover {
  background-color: rgba(59, 130, 246, 0.05);
  border-color: rgba(59, 130, 246, 0.1);
}
.technical-key {
  font-family: monospace;
  font-size: 10px;
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.08);
  padding: 1px 5px;
  border-radius: 4px;
}

@media (max-width: 960px) { .page-hero { flex-direction: column; align-items: flex-start; } }
</style>
